// Centralized mock data service with relationships
import { sedi } from '../data/sedi'

export interface Client {
  id: number
  number: string
  name: string
  status: 'Prospect' | 'Attivo' | 'Non attivo'
  zone: string
  email: string
  // "Email Formazione" registered in the company registry
  trainingEmail?: string
  phone: string
  referent: string
  type: string[]
  clientOf?: string[]
  safetyReferent?: string
  employeesCount?: number
}

export interface Worker {
  id: number
  clientId: number
  name: string
  company: string
  role: string
  fiscalCode: string
  birthDate: string
  birthPlace: string
  birthProvince: string
  nationality: string
  email: string
  department: string
  sede: string
  employmentType: string
  contractType: string
  trainingStatus: 'In regola' | 'In scadenza' | 'Scaduto'
  healthStatus: 'Idoneo' | 'Non idoneo' | 'Parzialmente idoneo'
  nextExpiry: string
  safetyRoles: string[]
  code: string
}

export interface Contract {
  id: number
  number: string
  client: string
  product: string
  status: string
  amount: string
  expiry: string
  tacitRenewal: boolean
  provenienza: string
  corrispettivoProvenienza: string
  tipologia: string
  acceptanceDate?: string
  rejectionDate?: string
  rejectionReason?: string
  revokeDate?: string
  revokeReason?: string
}

// Generate realistic data
const generateClients = (count: number): Client[] => {
  const safetyReferents = ['Alessio', 'Lorenzo', 'Gian Franco', 'Tiziano']
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    number: `CLI-${String(i + 1).padStart(4, '0')}`,
    name:
      i % 3 === 0
        ? `Azienda ${i} SRL`
        : i % 3 === 1
          ? `Studio ${i} Associati`
          : `Impresa ${i} SPA`,
    status: i % 10 === 0 ? 'Non attivo' : i % 5 === 0 ? 'Prospect' : 'Attivo',
    zone: ['Arezzo', 'Valdarno', 'Casentino', 'Firenze'][i % 4],
    email: `info@azienda${i}.it`,
    trainingEmail: `formazione@azienda${i}.it`,
    phone: `0575 ${100000 + i}`,
    referent: `Referente ${i}`,
    type: i % 2 === 0 ? ['Seform'] : ['Sef Studio', 'Seform'],
    clientOf: i % 2 === 0 ? ['seform'] : ['sef_studio', 'seform'],
    safetyReferent: safetyReferents[i % 4],
    employeesCount: Math.floor(Math.random() * 50) + 5,
  }))
}

const italianMaleNames = [
  'Marco',
  'Luca',
  'Alessandro',
  'Giuseppe',
  'Andrea',
  'Francesco',
  'Matteo',
  'Davide',
  'Simone',
  'Roberto',
  'Paolo',
  'Stefano',
  'Giovanni',
  'Antonio',
  'Fabio',
  'Riccardo',
  'Daniele',
  'Massimo',
  'Claudio',
  'Enrico',
]

const italianFemaleNames = [
  'Maria',
  'Giulia',
  'Francesca',
  'Sara',
  'Elena',
  'Chiara',
  'Valentina',
  'Alessandra',
  'Silvia',
  'Laura',
  'Paola',
  'Roberta',
  'Monica',
  'Federica',
  'Cristina',
  'Anna',
  'Lucia',
  'Marta',
  'Elisa',
  'Claudia',
]

const italianSurnames = [
  'Rossi',
  'Bianchi',
  'Ferrari',
  'Esposito',
  'Romano',
  'Colombo',
  'Ricci',
  'Marino',
  'Greco',
  'Bruno',
  'Gallo',
  'Conti',
  'De Luca',
  'Mancini',
  'Costa',
  'Giordano',
  'Rizzo',
  'Lombardi',
  'Moretti',
  'Barbieri',
]

const departments = [
  'Produzione',
  'Amministrazione',
  'Logistica',
  'Commerciale',
  'Manutenzione',
  'Qualità',
  'Magazzino',
  'Ufficio tecnico',
]


const generateWorkers = (
  clients: Client[],
  countPerClient: number,
): Worker[] => {
  let workers: Worker[] = []
  let idCounter = 1

  const provinces = ['AR', 'FI', 'SI', 'PG', 'RM', 'MI', 'BO', 'PO']
  const employmentTypes = [
    'Dipendente',
    'Dipendente',
    'Dipendente',
    'Amministratore',
    'Dirigente',
    'Collaboratore',
    'Dipendente',
    'Altro',
  ]
  const contractTypes = [
    'Indeterminato',
    'Indeterminato',
    'Determinato',
    'Indeterminato',
    'Part-time',
    'Collaborazione',
    'Indeterminato',
    'Stage',
  ]

  clients.forEach((client) => {
    for (let i = 0; i < countPerClient; i++) {
      const isFemale = (idCounter + i) % 3 === 0
      const firstName = isFemale
        ? italianFemaleNames[(idCounter + i) % italianFemaleNames.length]
        : italianMaleNames[(idCounter + i) % italianMaleNames.length]
      const surname =
        italianSurnames[(idCounter * 3 + i * 7) % italianSurnames.length]
      const fullName = `${firstName} ${surname}`

      const birthYear = 1965 + ((idCounter + i * 3) % 35)
      const birthMonth = String(((idCounter + i) % 12) + 1).padStart(2, '0')
      const birthDay = String(((idCounter * 2 + i) % 28) + 1).padStart(2, '0')

      const expiryMonth = ((idCounter + i * 2) % 12) + 1
      const expiryYear = 2025 + ((idCounter + i) % 2)
      const expiryDay = ((idCounter * 3 + i) % 28) + 1

      const roles: string[] = []
      if ((idCounter + i) % 5 === 0) roles.push('rls')
      if ((idCounter + i) % 4 === 0) roles.push('preposto')
      if ((idCounter + i) % 6 === 0) roles.push('antincendio')
      if ((idCounter + i) % 7 === 0) roles.push('primosoccorso')

      const birthPlaceIndex = (idCounter + i) % 8

      // Assign 'Parzialmente idoneo' to workers with id 3 and 7
      let healthSt: 'Idoneo' | 'Non idoneo' | 'Parzialmente idoneo' = 'Idoneo'
      if (idCounter === 3 || idCounter === 7) {
        healthSt = 'Parzialmente idoneo'
      } else if ((idCounter + i) % 10 === 0) {
        healthSt = 'Non idoneo'
      }

      workers.push({
        id: idCounter++,
        clientId: client.id,
        name: fullName,
        company: client.name,
        role: i % 3 === 0 ? 'Operaio' : i % 3 === 1 ? 'Impiegato' : 'Tecnico',
        fiscalCode: `${surname.substring(0, 3).toUpperCase()}${firstName.substring(0, 3).toUpperCase()}${String(birthYear).substring(2)}A${birthDay}A${String(100 + idCounter)}B`,
        birthDate: `${birthYear}-${birthMonth}-${birthDay}`,
        birthPlace: [
          'Arezzo',
          'Firenze',
          'Siena',
          'Perugia',
          'Roma',
          'Milano',
          'Bologna',
          'Prato',
        ][birthPlaceIndex],
        birthProvince: provinces[birthPlaceIndex],
        nationality:
          (idCounter + i) % 15 === 0
            ? 'Rumena'
            : (idCounter + i) % 20 === 0
              ? 'Albanese'
              : 'Italiana',
        email: `${firstName.toLowerCase()}.${surname.toLowerCase()}@email.it`,
        department: departments[(idCounter + i) % departments.length],
        sede: sedi[(idCounter + i) % sedi.length],
        employmentType:
          employmentTypes[(idCounter + i) % employmentTypes.length],
        contractType: contractTypes[(idCounter + i) % contractTypes.length],
        trainingStatus:
          Math.random() > 0.8
            ? 'Scaduto'
            : Math.random() > 0.6
              ? 'In scadenza'
              : 'In regola',
        healthStatus: healthSt,
        nextExpiry: `${String(expiryDay).padStart(2, '0')}/${String(expiryMonth).padStart(2, '0')}/${expiryYear}`,
        safetyRoles: roles,
        code: `LAV-${String(idCounter).padStart(4, '0')}`,
      })
    }
  })
  return workers
}

const generateContracts = (clients: Client[], count: number): Contract[] => {
  const products = [
    'Consulenza Sicurezza',
    'HACCP Base',
    'Privacy GDPR',
    'Formazione Lavoratori',
    'DVR',
  ]
  const statuses = [
    'da_proporre',
    'in_valutazione',
    'da_sollecitare',
    'accettato',
    'non_accettato',
    'revocato',
  ]
  const provenienze = ['Commerciale SEF', 'Esterni e partner', 'Altro']
  const tipologie = ['Sicurezza', 'Privacy', 'HACCP', 'Altro']

  return Array.from({ length: count }, (_, i) => {
    const year = i < 70 ? 25 : 24
    const seqNum = (i % 70) + 1
    const code = `${year}.${String(seqNum).padStart(3, '0')}`
    const status = statuses[i % statuses.length]
    const corrispettivo = `€ ${((i % 9) + 1) * 100},00`

    const contract: Contract = {
      id: i + 1,
      number: code,
      client: clients[i % clients.length].name,
      product: products[i % products.length],
      status,
      amount: `€ ${(Math.random() * 5000 + 1000).toFixed(2)}`,
      expiry: `${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}/${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}/2025`,
      tacitRenewal: i % 3 !== 0,
      provenienza: provenienze[i % provenienze.length],
      corrispettivoProvenienza: corrispettivo,
      tipologia: tipologie[i % tipologie.length],
    }

    if (status === 'accettato') {
      contract.acceptanceDate = '15/03/2025'
    }
    if (status === 'non_accettato') {
      contract.rejectionDate = '20/02/2025'
      contract.rejectionReason = 'Budget insufficiente'
    }
    if (status === 'revocato') {
      contract.revokeDate = '10/01/2025'
      contract.revokeReason = 'Cambio fornitore'
    }

    return contract
  })
}

// Singleton data store
class MockDataService {
  private clients: Client[] = []
  private workers: Worker[] = []
  private contracts: Contract[] = []

  constructor() {
    this.clients = generateClients(50)
    this.workers = generateWorkers(this.clients, 5) // ~250 workers
    this.contracts = generateContracts(this.clients, 100)
  }

  getClients() {
    return this.clients
  }
  getClient(id: number) {
    return this.clients.find((c) => c.id === id)
  }
  getClientByName(name: string) {
    return this.clients.find((c) => c.name === name)
  }
  setClientStatus(name: string, status: Client['status']) {
    this.clients = this.clients.map((c) =>
      c.name === name ? { ...c, status } : c,
    )
  }

  getWorkers() {
    return this.workers
  }
  getWorkersByClient(clientId: number) {
    return this.workers.filter((w) => w.clientId === clientId)
  }

  getContracts() {
    return this.contracts
  }

  // Add other getters as needed
}

export const mockData = new MockDataService()
